/**
 * El perfil de otra persona.
 *
 * ES EL DESTINO DE TODA FILA DE JUGADOR EN LA APP. Las tablas de gente —los
 * del club, los inscritos, los de Retos, las solicitudes— muestran cada una
 * lo que hace falta para SU decisión y nada más; cuando la pregunta es
 * «¿quién es esta persona?», la respuesta está acá y no repetida cuatro
 * veces con cuatro recortes distintos.
 *
 * LO QUE SE MUESTRA LO ELIGIÓ EL SERVIDOR, no esta pantalla. El correo, el
 * documento, la dirección y la coordenada no llegan hasta acá: no hay nada
 * que ocultar en el dibujo porque nunca viajó. Ver `PlayersService`.
 *
 * EL TELÉFONO PUEDE VENIR VACÍO aunque la persona lo tenga cargado: viene
 * vacío cuando apagó «quiero recibir retos». Cuando falta, se dice por qué;
 * un botón de WhatsApp ausente sin explicación se lee como que la pantalla
 * está a medio cargar.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Link, useParams } from 'react-router-dom';
import { divisionOf } from '@tennisup/contracts';
import { categoryKey } from '@tennisup/contracts';
import type { PointAdjustment, PublicPlayer, PublicPlayerClub } from '@tennisup/contracts';

import { Alert } from '../components/Alert';
import { AppLayout } from '../components/AppLayout';
import { BackButton } from '../components/BackButton';
import { Avatar } from '../components/Avatar';
import { Achievements } from '../components/Achievements';
import { EmergencyInfoCard } from '../components/EmergencyInfoCard';
import { ChallengeButtons } from '../components/ChallengeButtons';
import { MatchHistory } from '../components/MatchHistory';
import { DivisionIcon, WhatsAppIcon } from '../components/icons';
import { useAuth } from '../auth/AuthContext';
import { api, assetUrl } from '../lib/api';
import { errorMessage } from '../lib/errorMessage';
import { whatsappHref } from '../lib/whatsapp';
import { rankingName } from '../lib/categoryName';

/**
 * LA IDENTIDAD DE UNA FILA ES LA TABLA, NO EL CLUB.
 *
 * `player.clubs` trae una fila POR RANKING: quien compite en B femenino y C
 * masculino del mismo club viene dos veces, con el mismo `communityId`. Con
 * `key={club.communityId}` React tiene dos hijos con la misma llave, deja de
 * poder emparejarlos entre redibujados y termina duplicando o dejando pegadas
 * filas viejas — el mismo error que en la lista de jugadores del club.
 *
 * La parte de la categoría la arma `categoryKey`, que es la única que puede
 * armarla: una tabla de esta pantalla es «club + categoría».
 */
function llaveDeTabla(club: PublicPlayerClub): string {
  // `categoryKey` arma la parte de la categoría —hay una prueba que prohíbe
  // escribirla a mano, y me atajó acá mismo—. El club va delante porque en
  // esta pantalla una fila es «club + categoría», no solo categoría.
  return `${club.communityId}/${categoryKey({ ...club, discipline: 'SINGLES' })}`;
}

/** Los dos tabs de la ficha. El de puntos solo existe para quien administra. */
type Vista = 'resumen' | 'puntos';

export function PublicPlayerPage(): JSX.Element {
  const { t } = useTranslation();
  const { session } = useAuth();
  const { id = '' } = useParams();
  const [player, setPlayer] = useState<PublicPlayer | null>(null);
  const [error, setError] = useState<string | null>(null);
  /**
   * QUÉ TAB ESTÁ ABIERTO.
   *
   * El historial de puntos es una pantalla aparte y no un panel que se
   * despliega: Javier lo pidió como tab, y además solo lo ve quien administra
   * el club, así que mezclarlo con el resumen obligaba a esconder media
   * pantalla según quién mira.
   */
  const [vista, setVista] = useState<Vista>('resumen');

  const load = useCallback(async () => {
    try {
      setPlayer(await api.findPlayer(id));
    } catch (caught) {
      setError(errorMessage(t, caught));
    }
  }, [id, t]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error !== null) {
    return (
      <AppLayout>
        <Alert kind="error">{error}</Alert>
      </AppLayout>
    );
  }

  if (player === null) {
    return (
      <AppLayout>
        <p className="help">{t('common.loading')}</p>
      </AppLayout>
    );
  }

  // Mirarse a uno mismo desde una fila es una salida sin puerta: la pantalla
  // donde SE EDITA es «Mi perfil», y desde acá no se edita nada. En vez de
  // esconder el caso, se ofrece el camino.
  const soyYo = session !== null && session.user.id === player.userId;

  /*
    LOS CLUBES DE ESTA PERSONA QUE YO ADMINISTRO.
    
    Es lo único que decide si existe el tab de historial: si no administro
    ninguno, el tab no se dibuja —y el servidor contesta 403 igual, así que la
    pantalla no está guardando el secreto, solo evita mostrar una puerta que
    no abre—.
  */
  const clubesQueAdministro = player.clubs.filter((club) => club.canManage);
  const puedoVerHistorial = clubesQueAdministro.length > 0;

  // El tab de historial desaparece si dejo de administrar: sin esto quedaría
  // seleccionado un tab que ya no está en la barra, y la pantalla en blanco.
  const vistaReal: Vista = puedoVerHistorial ? vista : 'resumen';

  return (
    <AppLayout>
      {/* LA QUE MÁS FALTA HACÍA. A una ficha de jugador se llega desde un
          ranking, desde un cuadro, desde una búsqueda de retos — y sin
          «Volver» la única salida era una pestaña de abajo, que es perder el
          lugar en la tabla que se estaba mirando. `navigate(-1)` devuelve
          justamente ahí; «Retos» es el respaldo para quien llegó con el
          enlace directo. */}
      <BackButton to="/retos" />
      <div className="player-head">
        <Avatar url={assetUrl(player.avatarPath)} name={player.displayName} size={72} />
        <div className="player-id">
          <h1>{player.displayName}</h1>
          {/* El nombre legal, debajo y en gris: sirve para reconocer a quién
              se está por escribirle, no para gritarlo. */}
          <p className="help">{player.fullName}</p>
          <p className="help">
            {[
              player.locationLabel,
              player.distanceKm === null ? null : t('challenges.away', { km: player.distanceKm }),
            ]
              .filter((parte) => parte !== null && parte !== '')
              .join(' · ')}
          </p>
        </div>
      </div>

      {soyYo && (
        <p className="help">
          <Link to="/perfil">{t('player.editMine')}</Link>
        </p>
      )}

      {/*
        LOS TABS SOLO APARECEN SI HAY DOS. Una barra de un solo tab es un
        título disfrazado: ocupa lugar, se puede tocar y no lleva a ningún
        lado. Quien no administra ningún club de esta persona ve la ficha
        exactamente como antes, sin barra.
      */}
      {puedoVerHistorial && (
        <div className="wall-tabs" role="tablist" aria-label={t('player.tabs')}>
          {(['resumen', 'puntos'] as const).map((cual) => (
            <button
              key={cual}
              type="button"
              role="tab"
              id={`tab-${cual}`}
              aria-selected={vistaReal === cual}
              aria-controls={`panel-${cual}`}
              className={vistaReal === cual ? 'wall-tab wall-tab-on' : 'wall-tab'}
              onClick={() => setVista(cual)}
            >
              {t(cual === 'resumen' ? 'player.tabSummary' : 'player.tabPoints')}
            </button>
          ))}
        </div>
      )}

      {vistaReal === 'puntos' ? (
        <div role="tabpanel" id="panel-puntos" aria-labelledby="tab-puntos">
          {/*
            UN HISTORIAL POR CLUB, no uno solo mezclado: los puntos de alguien
            en el club A y en el club B son dos rankings distintos, y sumarlos
            en una lista sola daría un número que no existe en ninguna parte.
          */}
          {clubesQueAdministro.map((club) => (
            <Puntos
              key={llaveDeTabla(club)}
              club={club}
              userId={player.userId}
              onChanged={() => void load()}
            />
          ))}
        </div>
      ) : (
      <div role="tabpanel" id="panel-resumen" aria-labelledby="tab-resumen">
      <section>
        <h2>{t('player.clubs')}</h2>
        {player.clubs.length === 0 ? (
          // EL NIVEL DECLARADO ES EL PLAN B Y NO EL PLAN A. Cuando hay club,
          // manda el club: ese nivel lo fijó alguien que la vio jugar. El
          // declarado lo dijo la persona de sí misma, y se muestra con esa
          // palabra al lado para que se sepa cuál se está leyendo.
          <p className="help">
            {player.declaredLevel === null
              ? t('player.noClubs')
              : t('player.onlyDeclared', {
                  level: t(`categoryLevel.${player.declaredLevel}`),
                })}
          </p>
        ) : (
          <div className="table-scroll">
            {/* COMPACTA: tres columnas cortas, sin foto ni nombres largos.
                Con el ancho mínimo de las tablas de gente esta se desplazaba
                de lado en un teléfono y escondía los puntos, que es el número
                que se viene a mirar. */}
            <table className="people-table table-compact">
              <thead>
                <tr>
                  <th scope="col">{t('player.club')}</th>
                  <th scope="col">{t('ranking.category')}</th>
                  <th scope="col" className="num">
                    {t('ranking.points')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {player.clubs.map((club) => (
                  <tr key={llaveDeTabla(club)}>
                    <td>
                      <Link to={`/clubes/${club.communityId}`}>{club.communityName}</Link>
                    </td>
                    {/* LA DIVISIÓN AL LADO DE LA LETRA, que es donde de
                        verdad hace falta: en la tabla de clubes se compara
                        «B» contra «C2» de un vistazo y las letras no dicen
                        cuál está más arriba sin acordarse de las seis. */}
                    <td className="level-cell">
                      <DivisionIcon
                        division={divisionOf(club.level)}
                        title={t(`division.${divisionOf(club.level)}`)}
                        size={16}
                      />
                      {/* LA CATEGORÍA ENTERA —«B · Femenino»— y no solo la
                          letra. Quien compite en dos tablas del mismo club
                          tiene dos filas acá, y con el nivel solo pueden
                          decir lo mismo: «B» y «B». Indistinguibles es como
                          se lee «la base tiene datos repetidos». */}
                      {rankingName(t, club)}
                    </td>
                    <td className="num">
                      {club.points}
                      {/* EL «¿POR QUÉ?» AL LADO DEL NÚMERO, que es donde se
                          hace la pregunta. Lleva al tab de arriba en vez de
                          abrir un panel acá abajo: el historial ahora es una
                          pantalla propia, y tenerlo en dos lugares a la vez
                          era la misma lista con dos formas de cerrarse. */}
                      {club.canManage && (
                        <button
                          type="button"
                          className="link points-why"
                          onClick={() => setVista('puntos')}
                        >
                          {t('adjustments.why')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      </section>

      {/* LOS LOGROS VAN DEBAJO DE LOS CLUBES Y ARRIBA DE TODO LO DEMÁS.
          Arriba de los clubes taparían el dato duro —dónde juega y con
          cuántos puntos— con una fila de iconos; abajo del historial de
          partidos no los vería nadie. */}
      <Achievements achievements={player.achievements} />

      {/*
        A QUIÉN LLAMAR SI LE PASA ALGO. Javier: «si alguien ve el perfil los
        contactos de emergencia deben de aparecer».

        ARRIBA DE LOS BOTONES DE RETAR y no al final: quien abre esto por una
        emergencia no puede tener que pasar por «Retar amistosamente» para
        llegar al teléfono. Si la persona no cargó nada, no se dibuja.
      */}
      <EmergencyInfoCard emergency={player.emergency} />

      {!soyYo && <ContactRow player={player} />}

      {/* LOS DOS BOTONES DE RETAR, JUSTO DEBAJO DEL DE WHATSAPP. Es lo mismo
          que el de arriba pero con memoria: queda anotado que se propuso, que
          dijeron que sí, y quién ganó. El de WhatsApp no se va: es el que
          resuelve el partido de esta tarde. */}
      {!soyYo && <ChallengeButtons player={player} />}

      {/* EL CARA A CARA, debajo del botón de contactar y antes del historial.
          Es la pregunta que uno se hace ANTES de escribirle: «¿cómo me va
          contra este?». El historial de abajo trae todos sus partidos contra
          todo el mundo; esto trae solo los nuestros. */}
      {!soyYo && (
        <p className="vs-link">
          <Link className="button-like secondary" to={`/jugadores/${player.userId}/vs`}>
            {t('rivalry.open')}
          </Link>
        </p>
      )}

      {/* EL HISTORIAL, ABAJO DEL TODO. Lo primero que se busca en el perfil de
          alguien es quién es y cómo contactarlo; el historial es lo que se
          mira después, con calma. */}
      <MatchHistory userId={player.userId} />
      </div>
      )}
    </AppLayout>
  );
}

/**
 * El botón de WhatsApp, o la razón de que no esté.
 *
 * Tres finales distintos y NINGUNO es el silencio: la persona apagó los
 * retos, la persona no dejó teléfono, o acá está el botón. Los dos primeros
 * se parecen desde afuera y no son lo mismo: uno se arregla esperando a que
 * la otra persona lo prenda, el otro no se arregla.
 */
function ContactRow({ player }: { player: PublicPlayer }): JSX.Element {
  const { t } = useTranslation();
  const href =
    player.whatsappPhone === null
      ? null
      : whatsappHref(
          player.whatsappPhone,
          // Con el apodo adentro: ver el mismo botón en `ChallengesPage`.
          t('challenges.message', { name: player.displayName }),
        );

  if (!player.availableForChallenges) {
    return <p className="help">{t('player.notAvailable')}</p>;
  }
  if (href === null) return <p className="help">{t('challenges.noPhone')}</p>;

  return (
    <p>
      <a
        className="button-like whatsapp"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={t('challenges.contactWith', { name: player.displayName })}
      >
        <WhatsAppIcon title="" size={18} />
        <span>{t('challenges.contact')}</span>
      </a>
    </p>
  );
}

/**
 * DE DÓNDE SALEN LOS PUNTOS DE ESTA PERSONA EN ESTE CLUB.
 *
 * QUÉ PROBLEMA RESUELVE. Los puntos se mueven sin que se haya jugado nada —una
 * penalidad del club, los puntos con los que alguien entró, una corrección— y
 * hasta acá la única respuesta a «¿por qué perdí cinco puntos?» era «no sé».
 * Un ranking que se mueve sin explicación es un ranking en el que nadie
 * confía. Javier: «si quiero se muestre en un historial los cambios manuales
 * de los puntos».
 *
 * VIVE EN SU PROPIO TAB, y SOLO LO VE QUIEN ADMINISTRA EL CLUB —lo pidió
 * Javier así—. El servidor contesta 403 al resto, incluida la propia persona:
 * la pantalla no guarda el secreto, solo evita mostrar una puerta que no abre.
 *
 * LO PRIMERO QUE DICE ES DE QUÉ CLUB ES, porque en el tab se dibuja uno por
 * cada club que administro y si no lo dijeran serían tres listas de números
 * sin nada que las distinga.
 *
 * SE PIDE AL ABRIR EL TAB Y NO AL CARGAR LA PÁGINA: es una consulta por club,
 * y casi nadie entra ahí. Pedirla siempre serían tres consultas de más en
 * cada perfil que alguien mira.
 */
function Puntos({
  club,
  userId,
  onChanged,
}: {
  club: PublicPlayerClub;
  userId: string;
  onChanged: () => void;
}): JSX.Element {
  const { t, i18n } = useTranslation();
  const [filas, setFilas] = useState<PointAdjustment[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  /**
   * `club.communityId` en las dependencias y no `club`: el objeto es nuevo en
   * cada carga del perfil y volvería a pedir el historial cada vez.
   */
  const comunidad = club.communityId;
  useEffect(() => {
    let vivo = true;
    setFilas(null);
    setError(null);
    void (async () => {
      try {
        const respuesta = await api.pointHistory(comunidad, userId);
        // `PointAdjustment[]` es una promesa sobre el contrato, no una
        // garantía sobre el cable: un servidor una versión atrás no conoce
        // esta ruta y puede contestar cualquier cosa.
        if (vivo) setFilas(Array.isArray(respuesta) ? respuesta : []);
      } catch (caught) {
        if (!vivo) return;
        setError(errorMessage(t, caught));
        setFilas([]);
      }
    })();
    return () => {
      vivo = false;
    };
  }, [comunidad, userId, t]);

  return (
    <div className="points-history">
      {/*
        EL CLUB **Y LA CATEGORÍA**. Decía solo el club, y con dos tablas en el
        mismo club salen DOS paneles con el título idéntico, uno encima del
        otro: quien va a sumar puntos no sabe en cuál está escribiendo. Javier:
        «aquí no dice en que categoria estoy dando los puntos».

        El mismo arreglo que la etiqueta de la lista de jugadores: lo que
        distingue las dos filas tiene que estar escrito en cada una.
      */}
      <h3>
        {t('adjustments.historyTitle', {
          club: `${club.communityName} · ${rankingName(t, club)}`,
        })}
      </h3>

      {error !== null && <Alert kind="error">{error}</Alert>}

      {filas === null ? (
        <p className="help">{t('common.loading')}</p>
      ) : filas.length === 0 ? (
        // NADA QUE EXPLICAR SE DICE. «No hay ajustes» y «esto no cargó» se ven
        // igual si la lista simplemente no aparece.
        <p className="help">{t('adjustments.noAdjustments')}</p>
      ) : (
        <ul className="points-history-list">
          {filas.map((fila) => (
            <li key={fila.id}>
              <strong className={fila.points < 0 ? 'danger' : undefined}>
                {fila.points > 0 ? `+${fila.points}` : fila.points}
              </strong>{' '}
              <span>{motivo(t, fila.reason)}</span>
              {fila.note !== null && <span className="help"> · {fila.note}</span>}
              <span className="help">
                {' · '}
                {fechaCorta(fila.at, i18n.language)}
                {/* «Lo hizo el sistema» y «no sé quién» son cosas distintas, y
                    se dicen distinto. */}
                {fila.byName === null ? ` · ${t('adjustments.bySystem')}` : ` · ${fila.byName}`}
              </span>
            </li>
          ))}
        </ul>
      )}

      {club.canManage && <Corregir club={club} userId={userId} onDone={onChanged} />}
    </div>
  );
}

/**
 * EL MOTIVO, Y UN MOTIVO DESCONOCIDO NO SE ESCUPE COMO CLAVE.
 *
 * El servidor puede agregar un motivo que esta versión de la pantalla todavía
 * no conoce —pasa cada vez que se publica el servidor antes que el navegador—,
 * y sin esto la persona leería «adjustments.reason.LO_QUE_SEA» al lado de sus
 * puntos. Vale más una palabra honesta y vaga que una clave cruda: el número y
 * la fecha, que es lo que se vino a ver, siguen ahí.
 */
function motivo(t: TFunction, reason: string): string {
  const clave = `adjustments.reason.${reason}`;
  const texto = t(clave);
  return texto === clave ? t('adjustments.reason.OTHER') : texto;
}

/**
 * LA FECHA EN EL IDIOMA DE LA PANTALLA. `toLocaleDateString()` sin locale usa
 * el del navegador, y en un teléfono en inglés el historial de un club tico
 * salía en «9/9/2026» mientras todo lo demás estaba en español.
 */
function fechaCorta(iso: string, locale: string): string {
  const cuando = new Date(iso);
  if (Number.isNaN(cuando.getTime())) return iso;
  try {
    return cuando.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    // Un idioma que `Intl` no conoce no puede tumbar el historial entero.
    return cuando.toLocaleDateString();
  }
}

/**
 * CORREGIR LOS PUNTOS A MANO. Solo quien administra ese club.
 *
 * SUMA O RESTA, NO FIJA UN TOTAL, y el campo lo dice con esas palabras. Fijar
 * el total obligaría a recalcular partidos, retos y penalidades; un «+30» es
 * una fila más y el total sigue saliendo de sumar.
 *
 * EL MOTIVO ES OBLIGATORIO. No es burocracia: es lo único que va a quedar
 * cuando alguien pregunte dentro de seis meses, y quien lo escribió tampoco se
 * va a acordar.
 */
function Corregir({
  club,
  userId,
  onDone,
}: {
  club: PublicPlayerClub;
  userId: string;
  onDone: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [puntos, setPuntos] = useState('');
  const [nota, setNota] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const n = Number.parseInt(puntos, 10);
  const listo = Number.isFinite(n) && n !== 0 && nota.trim() !== '';

  async function guardar(): Promise<void> {
    if (!listo || enviando) return;
    setEnviando(true);
    setError(null);
    try {
      /*
       * MANDA LA TABLA DE ESTA FILA, y no solo el número.
       *
       * Con dos tablas en el mismo club el servidor EXIGE que se diga en
       * cuál: sin esto contesta «decí en cuál» y no escribe nada. Cada
       * `Corregir` vive dentro de su fila, así que la tabla es la suya.
       */
      await api.adjustPoints(club.communityId, userId, n, nota.trim(), {
        level: club.level,
        sex: club.sex,
      });
      setPuntos('');
      setNota('');
      onDone();
    } catch (caught) {
      setError(errorMessage(t, caught));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="points-adjust">
      {error !== null && <Alert kind="error">{error}</Alert>}

      <div className="field">
        <label htmlFor={`adjust-points-${llaveDeTabla(club)}`}>{t('adjustments.adjust')}</label>
        <input
          id={`adjust-points-${llaveDeTabla(club)}`}
          type="number"
          inputMode="numeric"
          placeholder="+30"
          value={puntos}
          disabled={enviando}
          onChange={(e) => setPuntos(e.target.value)}
        />
        <p className="help">{t('adjustments.adjustHelp')}</p>
      </div>

      <div className="field">
        <label htmlFor={`adjust-note-${llaveDeTabla(club)}`}>{t('adjustments.adjustNote')}</label>
        <input
          id={`adjust-note-${llaveDeTabla(club)}`}
          type="text"
          autoComplete="off"
          maxLength={300}
          placeholder={t('adjustments.adjustNotePlaceholder')}
          value={nota}
          disabled={enviando}
          onChange={(e) => setNota(e.target.value)}
        />
      </div>

      <p className="button-row">
        <button type="button" disabled={!listo || enviando} onClick={() => void guardar()}>
          {enviando ? t('common.sending') : t('common.save')}
        </button>
      </p>
    </div>
  );
}
